import { createContext, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import InfiniteViewer from "react-infinite-viewer";
import { makeId, roundOffTo, sendAppEvent } from "../../Utils";
import { DEFAULT_CARD_SIZE, roundSize } from "./Card/index";
import { BackToProjectLink, StyledContainer } from "./styles";

import classnames from "classnames";
import SVG from "react-inlinesvg";
import { Link, useParams } from "react-router-dom";
import Menu from "../Menu";
import BottomMenu from "./BottomMenu";
import EditableTextCard from "./Card/EditableTextCard";
import ImageCard from "./Card/ImageCard";
import InstructionsCard from "./Card/InstructionsCard";
import LabelCard from "./Card/LabelCard";
import { ZOOM_RANGE } from "./constants";
import GroupSelectionBox from "./GroupSelectionBox";
import HelpModal from "./HelpModal";
import useCardSelection from "./hooks/useCardSelection";
import useKeyboardCommands from "./hooks/useKeyboardCommands";
import usePersistCanvas from "./hooks/usePersistCanvas";
import useScrollZoom from "./hooks/useScrollZoom";
import useSpaceDrag from "./hooks/useSpaceDrag";

import { debounce } from "lodash";

import { OUTLINE_CARD_TYPE, OUTLINE_HEADER_CARD_TYPE, TEXT_CARD_TYPE } from "../../../../src/shared/canvasConstants";
import useHelpkit from "../../hooks/useHelpkit";
import BottomRightMenu from "./BottomRightMenu";
import OutlineCard from "./Card/OutlineCard";
import OutlineHeaderCard from "./Card/OutlineHeaderCard";
import { sampleCanvas } from "./constants";
import GroupArrows from "./GroupArrows";
import useCanvasReducer, { DELETE_ACTION, DISTRIBUTE_EVENLY_ACTION, LOAD_ACTION } from "./hooks/useCanvasReducer";
import useConfirm from "./hooks/useConfirm";
import useCreateCards from "./hooks/useCreateCards";
import useDragAndDropCards from "./hooks/useDragAndDropCards";
import useOutlineMethods from "./hooks/useOutlineMethods";

export const CardContext = createContext();

export default function Canvas({
  user, //
  subscription, //
  userData,
  setSettingsOpen,
  settingsOpen,
  demo,
  setDemo,
  subscriptionJSON,
  editorOptions,
  setEditorOptions,
  sleekToken,
  setHelpOpen,
  helpOpen,
  hasInteractedWithCanvas,
  setHasInteractedWithCanvas,
  readOnly = false,
}) {
  // const [cards, setCards] = useImmer(null);

  // cache for recalculating what cards are selected
  const [selectionCache, setSelectionCache] = useState(makeId(4));

  // flushes the selection cache and forces recalculation of what is selected
  // this should be called or added as dependency for effects and methods that
  // could change based on selection changes
  const flushSelectionCache = () => setSelectionCache(makeId(4));

  const [getConfirmation, Confirmation] = useConfirm();

  // This is the main state object backing canvas. All actions go through the reducer
  const [state, dispatch] = useCanvasReducer();
  const cards = state.cards;
  const groups = state.groups;

  const [groupSelectionBox, setGroupSelectionBox] = useState(null);
  const [zoom, setZoom] = useState(1); // zoom level
  const [position, setPosition] = useState({ x: 0, y: 0 }); // position of the viewport
  // const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [debug, setDebug] = useState(false);

  const [, hideFront] = useHelpkit();

  const [isEditing, setIsEditing] = useState(false); // whether user is currently focused on a text field anywhere on the cards

  const viewportRef = useRef(null);
  const viewerRef = useRef(null);

  const { projectId } = useParams();

  const cardContentsString = cards
    ? Object.keys(cards)
        .map((id) => cards[id].content + cards[id].height)
        .join(",")
    : "null";

  const cardIdsString = cards ? Object.keys(cards).join(",") : "null";
  const groupIdsString = groups ? Object.keys(groups).join(",") : "null";

  /**
   * memoized array of ids of currently selected cards
   */
  const selectedCardIds = useMemo(() => {
    if (!cards) {
      return [];
    }

    const selected = [];
    Object.entries(cards).forEach(([key, value]) => {
      if (value.selected) {
        selected.push(key);
      }
    });

    return selected;
  }, [selectionCache, cardIdsString]);

  // apply the right background color directly the background-image
  // this ensures that for light mode it is white, a special case for canvas
  useEffect(() => {
    document.body.querySelector(".background-image").style = "background-image: var(--canvas-background)";

    return () => {
      document.body.querySelector(".background-image").style = "";
    };
  }, []);

  useEffect(() => {
    hideFront();
    sendAppEvent("Canvas - Opened");
  }, []);

  /**
   * Gets the highest index of all cards
   * TODO: this is naively used to keep setting to top zindex, but we should
   * only do it if the top card is NOT the highest
   */
  const getHighestIndex = useCallback(() => {
    let highest = 0;

    for (const id of Object.keys(cards)) {
      if (cards[id].index > highest) {
        highest = cards[id].index;
      }
    }

    return highest;
  }, [cards]);

  /**
   * Sets a new zoom
   * @param {Number} newZoom
   */
  const changeZoom = (newZoom) => {
    if (!viewerRef.current) {
      return;
    }

    if (!newZoom) {
      return;
    }
    if (newZoom > ZOOM_RANGE[1]) {
      newZoom = ZOOM_RANGE[1];
    }

    if (newZoom < ZOOM_RANGE[0]) {
      newZoom = ZOOM_RANGE[0];
    }

    viewerRef.current.setZoom(newZoom);
    setZoom(newZoom);
  };

  /**
   * Deletes all selected cards
   */
  const deleteSelectedCards = () => {
    deleteCards(selectedCardIds);
  };

  /**
   * Deletes cards with specific ids
   */
  const deleteCards = async (ids) => {
    console.log("deleteCards", ids);
    if (ids.length > 0) {
      let groupDelete = false;

      ids.forEach((id) => {
        // For outline header cards, we delete all of the group's cards
        if (cards[id].type === OUTLINE_HEADER_CARD_TYPE) {
          groupDelete = true;
        }
      });

      if (groupDelete) {
        const status = await getConfirmation("Are you sure you want to delete your entire outline?");

        if (!status) {
          return;
        }
      }

      dispatch({ type: DELETE_ACTION, ids: ids });

      flushSelectionCache();

      setIsEditing(false);
    }
  };

  const injectSampleCanvas = () => {
    console.log("injecting sample");
    dispatch({ type: LOAD_ACTION, cards: sampleCanvas.cards, groups: sampleCanvas.groups });
    changeZoom(sampleCanvas.zoom);
    viewerRef.current.scrollTo(sampleCanvas.position.x, sampleCanvas.position.y);
  };

  // Backend persistence for cards
  const {
    loading,
    saved,
    canvasIdRef,
    error: persistError,
  } = usePersistCanvas({
    dispatch,
    readOnly,
    cards: state.cards,
    groups: state.groups,
    // setGroups,
    user,
    projectId,
    position,
    injectSampleCanvas,
    hasInteractedWithCanvas,
    setCanvasPosition: (pos) => {
      if (viewerRef.current) {
        // set canvas location on load
        viewerRef.current.scrollTo(pos.x, pos.y);
      }
    },
    setZoom: changeZoom,
    zoom: zoom,
  });

  // Handles dragging canvas on mousemove while space is pressed
  const { isSpaceDragging, isSpaceDown } = useSpaceDrag({
    onDrag: (dX, dY) => {
      if (viewerRef.current) {
        viewerRef.current.scrollBy(-dX * (1 / zoom), -dY * (1 / zoom));
      }
    },
  });

  useScrollZoom({ changeZoom: changeZoom, zoom: zoom });

  // // Methods related to card selection
  const { selectAllCards, deselectAllCards, selectAdditionalCard, DragSelection, isDraggingSelection } = useCardSelection({
    selectedCardIds,
    position,
    cards,
    groups,
    isSpaceDragging,
    cardIdsString,
    groupIdsString,
    getHighestIndex,
    zoom,
    dispatch,
    flushSelectionCache,
    selectionCache,
  });

  const { onDragEnd, onDragging } = useDragAndDropCards({
    cards,
    cardIdsString,
    flushSelectionCache,
    deleteCards,
    dispatch,
  });

  // // Card creation methods
  const { addCard, addGeneratedCard, addImageCard, addOutlineGroup } = useCreateCards({
    dispatch,
    position,
    zoom,
    cards,
    groups,
    flushSelectionCache,
    setHasInteractedWithCanvas,
    getHighestIndex,
    cardIdsString,
    subscriptionJSON,
    canvasId: canvasIdRef.current,
    groupIdsString,
    deleteCards,
  });

  const { generateOutline } = useOutlineMethods({
    cards,
    groups,
    groupIdsString,
    cardIdsString,
    dispatch,
  });

  useKeyboardCommands({
    dispatch,
    isEditing, //
    cards,
    zoom,
    changeZoom,
    deleteSelectedCards,
    selectedCardIds,
    selectAllCards,
    debug,
    setDebug,
    scrollToCenter: () => {
      viewerRef.current.scrollTo(0, 0);
    },
  });

  // Group selection box
  useEffect(() => {
    const numberSelected = selectedCardIds.length;

    if (numberSelected > 1) {
      const PADDING = 10;
      // show floating menu

      // calculate bounding box

      let top = null;
      let left = null;
      let right = null;
      let bottom = null;

      for (const id of selectedCardIds) {
        const card = cards[id];
        if (!top || card.y < top) {
          top = card.y;
        }
        if (!left || card.x < left) {
          left = card.x;
        }

        const calcRight = card.x + roundSize(card.width);
        const calcBottom = card.y + (card.autoHeight ? card.height : roundSize(card.height)); // when auto height, we don't round

        if (!right || calcRight > right) {
          right = calcRight;
        }
        if (!bottom || calcBottom > bottom) {
          bottom = calcBottom;
        }
      }

      // console.log({ top, left, right, bottom });

      // console.log({
      //   x: left - PADDING,
      //   y: top - PADDING,
      //   width: right - left + 2 * PADDING,
      //   height: bottom - top + 2 * PADDING,
      // });

      setGroupSelectionBox({
        x: left - PADDING,
        y: top - PADDING,
        width: right - left + 2 * PADDING,
        height: bottom - top + 2 * PADDING,
      });
    } else {
      setGroupSelectionBox(null);
    }
  }, [cards, selectionCache, selectedCardIds]);

  // this debounced function distributes all cards that have distribute marked as true
  const distribute = useCallback(
    debounce(() => {
      const c = Object.keys(cards)
        .map((id) => cards[id])
        .filter((card) => !!card.distribute);

      if (c.length > 0) {
        dispatch({ type: DISTRIBUTE_EVENLY_ACTION, ids: c.map((card) => card.id) });

        // distributeHeightsEvenly(c.map((card) => card.id));

        // setCards((draft) => {
        //   c.forEach((card) => {
        //     delete draft[card.id].distribute;
        //   });
        // });
      }
    }, 250),
    [cardContentsString, cardIdsString],
  );

  // distribute the cards evenly that are required (after generation)
  useEffect(() => {
    if (cards) {
      distribute();
    }
  }, [cardContentsString]);

  // if (cards) {
  //   console.log(`card-${Object.keys(cards)[0]}`);
  //   console.log(`card-${Object.keys(cards)[1]}`);
  // }

  // let selectBoxInCanvasCoordinates;
  // if (selectionBox) {
  //   selectBoxInCanvasCoordinates = globalToCanvasCoordinates(selectionBox);
  // }

  // calculate the dot grid spacing and size
  const dotSpacing = zoom > 1 ? 20 : roundOffTo((1 / zoom) * 20, 20);
  const dotSize = zoom > 1 ? 1 : 1 / zoom;

  // Saved status text

  let savedStatus;
  if (loading) {
    savedStatus = null;
  } else {
    savedStatus = saved ? (
      <>
        Saved <i className="fas fa-check"></i>
      </>
    ) : (
      <>
        Saving <i className="fas fa-circle-notch"></i>
      </>
    );
  }

  const statusText = <Link to={`/app/project/${projectId}`}>Back to Project</Link>;

  return (
    <>
      <Confirmation />

      <BackToProjectLink
        as={Link}
        to={`/app/project/${projectId}`}
      >
        <SVG src="/assets/back-arrow.svg" />
        <span>Back to Project</span>
      </BackToProjectLink>
      {!readOnly && (
        <Menu
          subscription={subscription}
          userData={userData}
          setSettingsOpen={setSettingsOpen}
          settingsOpen={settingsOpen}
          demo={demo}
          setDemo={setDemo}
          subscriptionJSON={subscriptionJSON}
          editorOptions={editorOptions}
          setEditorOptions={setEditorOptions}
          sleekToken={sleekToken}
          setHelpOpen={setHelpOpen}
          helpOpen={helpOpen}
          statusText={statusText}
          floating={true}
          hideLabs
          hideHelp
          hideNew
          hideHiring
          isLoading={loading}
        />
      )}

      {!showHelp && !isSpaceDragging && !isEditing && <DragSelection />}

      <StyledContainer
        onMouseUp={(e) => {
          if (isSpaceDragging) {
            return false;
          }
          // Handle deselecting cards when clicking anywhere outside of card or floating menu context
          const closestCard = e.target.closest(".card");
          const closestFloating = e.target.closest(".floating-menu");

          if (!closestCard && !closestFloating) {
            // console.log("viewport mouseup deselect");
            deselectAllCards();
          }
        }}
        className={classnames({
          "is-space-dragging": isSpaceDragging,
        })}
      >
        {loading && <div className="loading">Loading...</div>}

        <BottomMenu
          addCard={addCard}
          addOutlineGroup={addOutlineGroup}
        />

        {/* <div className="selection-box-info">
        Selection Box:
        <div>top: {Math.round(selectBoxInCanvasCoordinates?.top) || ''}</div>
        <div>left: {Math.round(selectBoxInCanvasCoordinates?.left) || ''}</div>
        <div>width: {Math.round(selectBoxInCanvasCoordinates?.width) || ''}</div>
        <div>height: {Math.round(selectBoxInCanvasCoordinates?.height) || ''}</div>
        <div>zoom: {zoom}</div>
        <div>position: {Math.round(position[0])}, {Math.round(position[1])}</div>
      </div> */}

        <InfiniteViewer
          className="viewer"
          zoomRange={ZOOM_RANGE}
          usePinch={true}
          useAutoZoom={true}
          ref={viewerRef}
          onPinch={(e) => {
            setZoom(e.zoom);
          }}
          onScroll={(e) => {
            setPosition({ x: e.scrollLeft, y: e.scrollTop });
          }}
        >
          <div
            className="viewport invisible-scrollbar"
            ref={viewportRef}
          >
            <CardContext.Provider
              value={{
                debug,
                selectedCardIds,
                deselectAllCards,
                isSpaceDragging,
                isSpaceDown,
                setIsEditing,
                dispatch,
                addGeneratedCard,
                addImageCard,
                selectAdditionalCard,
                generateOutline,
                readOnly,
                onDragEnd,
                onDragging,
                cards,
                groups,
                canvasId: canvasIdRef.current,
                getConfirmation,
              }}
            >
              {cards &&
                Object.keys(cards).map((id, i) => {
                  const card = cards[id];

                  return (
                    <Fragment key={i}>
                      {card.type === "text" && (
                        <EditableTextCard
                          id={card.id}
                          zoom={zoom}
                          width={card.width}
                          height={card.height}
                          content={card.content}
                          key={card.id}
                          selected={card.selected}
                          x={card.x}
                          y={card.y}
                          generating={card.generating}
                          type={card.type}
                          index={card.index}
                          initialEditing={card.initialEditing}
                          autoHeight={card.autoHeight}
                        />
                      )}

                      {card.type === "outline" && (
                        <OutlineCard
                          id={card.id}
                          zoom={zoom}
                          width={card.width}
                          height={card.height}
                          title={card.title}
                          placeholder={card.placeholder}
                          content={card.content}
                          key={card.id}
                          selected={card.selected}
                          x={card.x}
                          y={card.y}
                          generating={card.generating}
                          type={card.type}
                          index={card.index}
                          initialEditing={card.initialEditing}
                          autoHeight={card.autoHeight}
                          droppingOn={card.droppingOn}
                          data={card.data}
                        />
                      )}

                      {card.type === "outline_header" && (
                        <OutlineHeaderCard
                          id={card.id}
                          zoom={zoom}
                          width={card.width}
                          height={card.height}
                          title={card.title}
                          placeholder={card.placeholder}
                          content={card.content}
                          key={card.id}
                          selected={card.selected}
                          x={card.x}
                          y={card.y}
                          generating={card.generating}
                          type={card.type}
                          index={card.index}
                          initialEditing={card.initialEditing}
                          autoHeight={card.autoHeight}
                          data={card.data}
                        />
                      )}

                      {card.type === "image" && (
                        <ImageCard
                          id={card.id}
                          zoom={zoom}
                          width={card.width}
                          height={card.height}
                          content={card.content}
                          key={card.id}
                          selected={card.selected}
                          x={card.x}
                          y={card.y}
                          generating={card.generating}
                          type={card.type}
                          index={card.index}
                          autoHeight={card.autoHeight}
                        />
                      )}

                      {card.type === "label" && (
                        <LabelCard
                          id={card.id}
                          zoom={zoom}
                          width={card.width}
                          height={card.height}
                          content={card.content}
                          key={card.id}
                          selected={card.selected}
                          x={card.x}
                          y={card.y}
                          generating={card.generating}
                          type={card.type}
                          index={card.index}
                          initialEditing={card.initialEditing}
                          autoHeight={card.autoHeight}
                        />
                      )}

                      {card.type === "instructions" && (
                        <InstructionsCard
                          id={card.id}
                          zoom={zoom}
                          width={card.width}
                          height={card.height}
                          key={card.id}
                          selected={card.selected}
                          x={card.x}
                          y={card.y}
                          generating={card.generating}
                          type={card.type}
                          index={card.index}
                          autoHeight={card.autoHeight}
                        />
                      )}
                    </Fragment>
                  );
                })}
            </CardContext.Provider>

            {groupSelectionBox && (
              <GroupSelectionBox
                x={groupSelectionBox.x}
                y={groupSelectionBox.y}
                width={groupSelectionBox.width}
                height={groupSelectionBox.height}
                // only show the menu when it's a mix of outline and text cards
                showMenu={selectedCardIds.filter((id) => [TEXT_CARD_TYPE, OUTLINE_CARD_TYPE].includes(cards[id].type)).length > 0}
                addGeneratedCard={() => {
                  addGeneratedCard({
                    x: groupSelectionBox.x + groupSelectionBox.width + 20,
                    y: groupSelectionBox.y + groupSelectionBox.height / 2 - DEFAULT_CARD_SIZE[1] / 2,
                    generated: true,
                    sourceCardIds: selectedCardIds,
                    ids: selectedCardIds,
                    width: DEFAULT_CARD_SIZE[0],
                    height: DEFAULT_CARD_SIZE[1],
                  });

                  deselectAllCards();
                }}
                addImageCard={() => {
                  const prompt = selectedCardIds
                    .filter((id) => cards[id].type === "text")
                    .map((id) => {
                      return cards[id].content;
                    });

                  addImageCard({
                    x: groupSelectionBox.x + groupSelectionBox.width + 20,
                    y: groupSelectionBox.y + 20,
                    prompt: prompt,
                    width: DEFAULT_CARD_SIZE[0],
                    height: DEFAULT_CARD_SIZE[1],
                  });

                  deselectAllCards();
                }}
                zoom={zoom}
              />
            )}

            {cards && groups && (
              <GroupArrows
                groups={groups}
                cards={cards}
              />
            )}

            <div
              className="dots"
              style={{
                backgroundSize: `${dotSpacing}px ${dotSpacing}px`,
                backgroundImage: `radial-gradient(var(--canvas-dots-color) ${dotSize}px, transparent 0)`,
              }}
            ></div>
          </div>
        </InfiniteViewer>

        {persistError && <div className="error">{persistError}</div>}

        <BottomRightMenu
          changeZoom={changeZoom}
          zoom={zoom}
          setShowHelp={setShowHelp}
          dispatch={dispatch}
        />

        {showHelp && <HelpModal setShowHelp={setShowHelp} />}

        {debug && (
          <div className="debug-panel">
            <div>pos.x: {Math.round(position.x)} </div>
            <div>pos.y: {Math.round(position.y)} </div>
            <div>zoom: {zoom.toFixed(2)} </div>
            <div>isEditing: {isEditing ? "true" : "false"} </div>
            <div># cards: {Object.keys(cards).length}</div>
            <div>
              <a onClick={(e) => injectSampleCanvas()}>Inject default template</a>
            </div>
            <div>
              <a
                onMouseOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dispatch({ type: DISTRIBUTE_EVENLY_ACTION, ids: selectedCardIds });
                }}
              >
                dist vert
              </a>
            </div>
          </div>
        )}
      </StyledContainer>
    </>
  );
}
